/**
 * Planet cloud layer.
 *
 * Two styles, chosen per archetype in PLANET_CLOUDS:
 * - 'puffs': low-poly cloud clusters (flattened icosahedra) as ONE InstancedMesh
 *   per planet, placed where a deterministic noise field exceeds the coverage
 *   threshold. Matches the flat-shaded world.
 * - 'haze': a translucent shader shell whose alpha is drifting noise. Softer,
 *   for dust and spore worlds.
 *
 * Purely visual: clouds never raycast, never enter game state, and are not
 * fogged (they carry no information about what is under them). Deterministic
 * per planet id so both peers see the same sky.
 *
 * 67.47 (the owner: "clouds follow wind"; "there are some high atmosphere clouds
 * that drift and I do like them, viridis has them"): over the planet the camera
 * is on, the layer drifts the way the wind blows there and as hard — its own speed
 * × the wind's strength with its gusts (`setCloudWind`, from the wind field at the
 * camera each frame, via the weather view). Without the field (the turn clock) it keeps its own slow
 * turn. One vector and one number a frame.
 *
 * Design reference: design_sheets/clouds.html
 */

import * as THREE from 'three';
import type { PlanetState, PlanetArchetype, CloudProfile } from '../types';
import { PLANET_CLOUDS } from '../constants';
import { SeededRandom } from '../core/random';
import { positionJitter } from '../core/mapgen';

export interface CloudLayer {
  planetId: number;              // 67.47: whose sky (the wind at the camera drives the one over it)
  group: THREE.Group;            // at the planet position; rotates slowly
  mesh: THREE.InstancedMesh | THREE.Mesh;
  profile: CloudProfile;
  count: number;                 // puffs (0 for haze)
  material: THREE.Material;
  axis: THREE.Vector3;           // rotation axis (slightly tilted)
}

export interface CloudOptions {
  coverage?: number;   // multiplier on the profile's coverage
  altitude?: number;   // override, fraction of radius
  puffScale?: number;  // multiplier
  opacity?: number;    // override
  style?: CloudProfile['style'];
}

/** Cloud profile for a planet: by palette name (earth), then archetype; undefined = clear skies. */
export function cloudProfileFor(planet: PlanetState): CloudProfile | undefined {
  const byName = (PLANET_CLOUDS as Record<string, CloudProfile | undefined>)[planet.palette.name];
  if (planet.palette.name === 'earth' && byName) return byName;
  return PLANET_CLOUDS[planet.archetype as PlanetArchetype] ?? byName;
}

// Deterministic 3D value noise in [-1, 1] (same construction as planetColors)
function valueNoise(x: number, y: number, z: number): number {
  const x0 = Math.floor(x), y0 = Math.floor(y), z0 = Math.floor(z);
  const fx = x - x0, fy = y - y0, fz = z - z0;
  const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy), sz = fz * fz * (3 - 2 * fz);
  const c = (i: number, j: number, k: number) => positionJitter((x0 + i) * 0.37, (y0 + j) * 0.37, (z0 + k) * 0.37);
  const l = (a: number, b: number, s: number) => a + (b - a) * s;
  return l(
    l(l(c(0, 0, 0), c(1, 0, 0), sx), l(c(0, 1, 0), c(1, 1, 0), sx), sy),
    l(l(c(0, 0, 1), c(1, 0, 1), sx), l(c(0, 1, 1), c(1, 1, 1), sx), sy),
    sz,
  );
}
function fbm(x: number, y: number, z: number): number {
  let v = 0, a = 0.5, f = 1;
  for (let i = 0; i < 3; i++) { v += a * valueNoise(x * f, y * f, z * f); a *= 0.5; f *= 2.1; }
  return v; // roughly [-0.9, 0.9]
}

const PUFF_GEO = new THREE.IcosahedronGeometry(1, 1);
PUFF_GEO.scale(1, 0.55, 1);
PUFF_GEO.userData.shared = true;

const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _pos = new THREE.Vector3();
const _scl = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);

/**
 * Where the puffs go: candidate directions on a Fibonacci sphere, kept where the
 * noise field (offset by the planet id) exceeds the coverage threshold. Each kept
 * site becomes a small cluster. Returns instance transforms in planet-local space.
 */
export function layoutPuffs(planet: PlanetState, profile: CloudProfile, opts: CloudOptions = {}): THREE.Matrix4[] {
  const R = planet.radius;
  const coverage = Math.max(0, Math.min(1, profile.coverage * (opts.coverage ?? 1)));
  const altitude = opts.altitude ?? profile.altitude;
  const puffScale = profile.puffScale * (opts.puffScale ?? 1);
  const rng = new SeededRandom(`clouds-${planet.id}-${planet.palette.name}`);
  const candidates = Math.max(24, Math.min(900, Math.round(70 * (R / 5) * (R / 5))));
  const seedOff = positionJitter(planet.id * 0.123 + 0.5, 0.25, 0.75) * 50;
  const threshold = 0.9 - coverage * 1.6;   // coverage 0 → nothing passes, 1 → almost everything
  const out: THREE.Matrix4[] = [];
  const golden = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < candidates; i++) {
    const y = 1 - (2 * (i + 0.5)) / candidates;
    const r = Math.sqrt(Math.max(0, 1 - y * y));
    const a = golden * i;
    const dx = Math.cos(a) * r, dy = y, dz = Math.sin(a) * r;
    const n = fbm(dx * 2.2 + seedOff, dy * 2.2 + seedOff * 0.7, dz * 2.2 - seedOff * 0.3);
    if (n < threshold) continue;
    const strength = Math.min(1, (n - threshold) / 0.5);   // 0 at the edge of a cloud, 1 deep inside
    const puffs = 1 + Math.round(strength * 2 + rng.range(0, 1));
    for (let p = 0; p < puffs; p++) {
      // small tangent-plane scatter for the extra puffs
      let ox = 0, oy = 0, oz = 0;
      if (p > 0) {
        const t = rng.range(0, Math.PI * 2), d = rng.range(0.4, 1.0) * puffScale * 0.9;
        // tangent basis
        const ax = Math.abs(dy) < 0.9 ? 0 : 1, ay = Math.abs(dy) < 0.9 ? 1 : 0;
        let tx = ay * dz - 0 * dy, ty = 0 * dx - ax * dz, tz = ax * dy - ay * dx;
        const tl = Math.hypot(tx, ty, tz) || 1; tx /= tl; ty /= tl; tz /= tl;
        const bx = dy * tz - dz * ty, by = dz * tx - dx * tz, bz = dx * ty - dy * tx;
        ox = (tx * Math.cos(t) + bx * Math.sin(t)) * d;
        oy = (ty * Math.cos(t) + by * Math.sin(t)) * d;
        oz = (tz * Math.cos(t) + bz * Math.sin(t)) * d;
      }
      const alt = R * (1 + altitude) + rng.range(-0.02, 0.06) * R;
      _pos.set(dx * alt + ox, dy * alt + oy, dz * alt + oz);
      const dir = _pos.clone().normalize();
      _q.setFromUnitVectors(_up, dir);
      const s = puffScale * (0.6 + 0.5 * strength) * rng.range(0.75, 1.25) * (p === 0 ? 1 : 0.75);
      _scl.set(s * rng.range(0.85, 1.3), s, s * rng.range(0.85, 1.3));
      out.push(new THREE.Matrix4().compose(_pos, _q, _scl));
    }
  }
  return out;
}

const HAZE_VERT = /* glsl */`
varying vec3 vN;
varying vec3 vP;
void main() {
  vN = normalize(normalMatrix * normal);
  vP = position;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;
const HAZE_FRAG = /* glsl */`
uniform vec3 uColor;
uniform float uTime;
uniform float uCoverage;
uniform float uOpacity;
uniform float uSeed;
uniform vec3 uLightDir;
varying vec3 vN;
varying vec3 vP;
float hash3(vec3 p) { p = fract(p * 0.3183099 + vec3(0.1, 0.2, 0.3)); p *= 17.0; return fract(p.x * p.y * p.z * (p.x + p.y + p.z)); }
float noise3(vec3 x) {
  vec3 i = floor(x); vec3 f = fract(x); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(mix(hash3(i), hash3(i + vec3(1,0,0)), f.x), mix(hash3(i + vec3(0,1,0)), hash3(i + vec3(1,1,0)), f.x), f.y),
             mix(mix(hash3(i + vec3(0,0,1)), hash3(i + vec3(1,0,1)), f.x), mix(hash3(i + vec3(0,1,1)), hash3(i + vec3(1,1,1)), f.x), f.y), f.z);
}
float fbm(vec3 p) { float v = 0.0; float a = 0.5; for (int i = 0; i < 4; i++) { v += a * noise3(p); p = p * 2.03 + vec3(1.7, 9.2, 3.1); a *= 0.5; } return v; }
void main() {
  vec3 d = normalize(vP);
  float n = fbm(d * 2.6 + vec3(uSeed, uTime * 0.02, -uSeed * 0.5)) + 0.35 * fbm(d * 6.0 - vec3(uTime * 0.03, uSeed, 0.0));
  float a = smoothstep(0.78 - uCoverage * 0.5, 0.95 - uCoverage * 0.35, n) * uOpacity;
  float light = 0.55 + 0.45 * max(0.0, dot(normalize(vN), normalize(uLightDir)));
  gl_FragColor = vec4(uColor * light, a);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

/** Build the cloud layer for a planet, or null when its archetype has clear skies. */
export function createCloudLayer(planet: PlanetState, opts: CloudOptions = {}): CloudLayer | null {
  const profile = cloudProfileFor(planet);
  if (!profile) return null;
  const style = opts.style ?? profile.style;
  const group = new THREE.Group();
  group.position.set(planet.position.x, planet.position.y, planet.position.z);
  const axis = new THREE.Vector3(0.18, 1, -0.12).normalize();
  const color = new THREE.Color(profile.color);
  const opacity = opts.opacity ?? profile.opacity;

  if (style === 'haze') {
    const material = new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: color }, uTime: { value: 0 },
        uCoverage: { value: Math.max(0, Math.min(1, profile.coverage * (opts.coverage ?? 1))) },
        uOpacity: { value: opacity }, uSeed: { value: (planet.id % 13) * 1.7 },
        uLightDir: { value: new THREE.Vector3(40, 50, 30).normalize() },
      },
      vertexShader: HAZE_VERT, fragmentShader: HAZE_FRAG,
      transparent: true, depthWrite: false,
    });
    const mesh = new THREE.Mesh(new THREE.SphereGeometry(planet.radius * (1 + (opts.altitude ?? profile.altitude)), 48, 32), material);
    mesh.raycast = () => {};
    group.add(mesh);
    return { planetId: planet.id, group, mesh, profile, count: 0, material, axis };
  }

  const matrices = layoutPuffs(planet, profile, opts);
  const material = new THREE.MeshLambertMaterial({ color, flatShading: true, transparent: opacity < 1, opacity, depthWrite: opacity >= 0.98 });
  const mesh = new THREE.InstancedMesh(PUFF_GEO, material, Math.max(1, matrices.length));
  mesh.name = 'clouds';   // (every instanced layer named: a census or a picked object says what it is)
  mesh.count = matrices.length;
  for (let i = 0; i < matrices.length; i++) mesh.setMatrixAt(i, matrices[i]);
  mesh.instanceMatrix.needsUpdate = true;
  mesh.frustumCulled = false;
  mesh.raycast = () => {};      // clouds never catch clicks
  mesh.userData.noBake = true;
  group.add(mesh);
  return { planetId: planet.id, group, mesh, profile, count: matrices.length, material, axis };
}

/**
 * 67.47: how the wind drives the layer over the camera — × the layer's own speed: `least` in a calm, `perFactor` more for
 * each of the wind's factor (1 = the weather's usual wind there, more in a gust, up to 2).
 */
export const CLOUD_WIND = { least: 0.3, perFactor: 1.2 };
let windDrive: { planetId: number; axis: THREE.Vector3; rate: number } | null = null;
/**
 * 67.47: the wind at the camera, for the layer over it: `planet` the one the camera is on (null: none), `camera` its
 * position, `wind` the wind there along the ground (its direction; its length the factor: gust × shelter) — null without
 * the wind's field, and the layer keeps its own turn. The layer turns about up × the wind, so it passes overhead the way
 * the wind blows.
 */
export function setCloudWind(planet: Pick<PlanetState, 'id' | 'position'> | null, camera: { x: number; y: number; z: number }, wind: { x: number; y: number; z: number } | null): void {
  if (!planet || !wind) { windDrive = null; return; }
  const up = new THREE.Vector3(camera.x - planet.position.x, camera.y - planet.position.y, camera.z - planet.position.z).normalize();
  const w = new THREE.Vector3(wind.x, wind.y, wind.z), factor = w.length();
  const axis = new THREE.Vector3().crossVectors(up, w);
  if (factor < 1e-9 || axis.lengthSq() < 1e-12) { windDrive = { planetId: planet.id, axis: up, rate: 0 }; return; }   // calm here: still
  windDrive = { planetId: planet.id, axis: axis.normalize(), rate: CLOUD_WIND.least + CLOUD_WIND.perFactor * factor };
}

/** Per frame: drift the layer around its tilted axis (67.47: over the camera, the way the wind blows); advance the haze shader's time. */
export function updateCloudLayer(layer: CloudLayer, dt: number, time: number): void {
  if (windDrive && windDrive.planetId === layer.planetId) layer.group.rotateOnWorldAxis(windDrive.axis, dt * layer.profile.speed * windDrive.rate);
  else layer.group.rotateOnAxis(layer.axis, dt * layer.profile.speed);
  const sm = layer.material as THREE.ShaderMaterial;
  if (sm.uniforms?.uTime) sm.uniforms.uTime.value = time;
}

export function disposeCloudLayer(scene: THREE.Scene, layer: CloudLayer): void {
  scene.remove(layer.group);
  layer.material.dispose();
  if (layer.mesh.geometry !== PUFF_GEO) layer.mesh.geometry.dispose();
  if ((layer.mesh as THREE.InstancedMesh).isInstancedMesh) (layer.mesh as THREE.InstancedMesh).dispose();
}
